import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import {
    Button,
    Checkbox,
    Card,
    Col,
    Empty,
    Input,
    List,
    Row,
    Space,
    Table,
    message,
    Modal,
    Form,
    Select,
    DatePicker,
    Tag,
    Alert,
    Typography,
    Spin,
    Breadcrumb,
    Grid,
} from 'antd';
import { FileTextOutlined, ArrowLeftOutlined, HomeOutlined, DownOutlined, RightOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { Breakpoint } from 'antd/es/_util/responsiveObserver';
import axios from 'axios';
import dayjs, { Dayjs } from 'dayjs';
import { ENDPOINTS } from '../helpers/api/apiConfig';
import Report from '../components/Report';
import AreaMap from '../components/Map/AreaMap';
import ReportLatestDaySummaryCard from '../components/ReportSections/ReportLatestDaySummaryCard';
import ReportChatAssistant from '../components/ReportChatAssistant';
import { trackActivity } from '../helpers/analytics/trackActivity';
import useLatestMeasurementSummaries from '../hooks/useLatestMeasurementSummaries';
import useAreaHealthStatuses from '../hooks/useAreaHealthStatuses';
import { HealthStatus, HEALTH_STATUS_CONFIG } from '../helpers/reports/areaHealthStatus';
import { useRoleControl } from '../helpers/roleControl';
import { useAuth } from '../context/AuthContext';
import '../style/Report.css';
import { useLocation, useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { useBreakpoint } = Grid;

interface AreaType {
    id: number;
    name: string;
}

interface Area {
    archivedAt?: string | null;
    id: number;
    name: string;
    companyId?: number | null;
    areaType?: AreaType | null;
    parentAreaId?: number | null;
    geom?: any;
    spaceType?: string;
    configurations?: Record<string, any> | null;
    children?: Area[];
    hierarchyDepth?: number;
}

interface ReportTemplate {
    id: number;
    key: string;
    name: string;
    description?: string;
    isGlobal: boolean;
    company?: {
        id: number;
        name: string;
    };
    sections: any[];
    areaTypeIds?: number[];
    metadata?: {
        appliesToAreaTypes?: number[];
    };
}

interface NavigationState {
    area: Area;
    template: ReportTemplate;
    dateRange: [Dayjs, Dayjs];
    patternId?: number | null;
    patternPointId?: number | null;
    measurementId?: number | null;
}

interface ReportTemplateState {
    template: ReportTemplate;
    areaId: number | null;
    dateFrom: Date;
    dateTo: Date;
    patternId?: number | null;
    patternPointId?: number | null;
    measurementId?: number | null;
}

type SlamPlacementLike = { lat: number; lng: number; bearingDegrees: number };

const DEG_TO_RAD = Math.PI / 180;
const EARTH_RADIUS = 6371000;

const isGpsCoordinate = (coord: any): boolean => {
    if (!Array.isArray(coord) || coord.length < 2) return false;
    const [lng, lat] = coord;
    return Number.isFinite(lng) && Number.isFinite(lat)
        && lng >= -180 && lng <= 180
        && lat >= -90 && lat <= 90;
};

const isGpsGeometry = (geom: any): boolean => {
    if (!geom?.coordinates) return false;
    if (geom.type === 'Point') return isGpsCoordinate(geom.coordinates);
    if (geom.type === 'Polygon') return Array.isArray(geom.coordinates?.[0]) && geom.coordinates[0].every(isGpsCoordinate);
    if (geom.type === 'MultiPolygon') return Array.isArray(geom.coordinates?.[0]?.[0]) && geom.coordinates[0][0].every(isGpsCoordinate);
    return false;
};

const localMetersToGps = (mx: number, my: number, anchor: SlamPlacementLike): [number, number] => {
    const bearing = anchor.bearingDegrees * DEG_TO_RAD;
    const northM = my * Math.cos(bearing) - mx * Math.sin(bearing);
    const eastM = my * Math.sin(bearing) + mx * Math.cos(bearing);
    const lat = anchor.lat + (northM / EARTH_RADIUS) * (180 / Math.PI);
    const lng = anchor.lng + (eastM / (EARTH_RADIUS * Math.cos(anchor.lat * DEG_TO_RAD))) * (180 / Math.PI);
    return [lng, lat];
};

const convertLocalGeometryToGps = (geom: any, anchor: SlamPlacementLike): any | null => {
    if (!geom?.coordinates) return null;
    if (geom.type === 'Point') {
        const [mx, my] = geom.coordinates;
        if (!Number.isFinite(mx) || !Number.isFinite(my)) return null;
        return { type: 'Point', coordinates: localMetersToGps(mx, my, anchor) };
    }
    if (geom.type === 'Polygon' && Array.isArray(geom.coordinates?.[0])) {
        return {
            type: 'Polygon',
            coordinates: [geom.coordinates[0].map(([mx, my]: [number, number]) => localMetersToGps(mx, my, anchor))],
        };
    }
    if (geom.type === 'MultiPolygon' && Array.isArray(geom.coordinates?.[0]?.[0])) {
        return {
            type: 'MultiPolygon',
            coordinates: geom.coordinates.map((poly: [number, number][][]) =>
                poly.map((ring) => ring.map(([mx, my]) => localMetersToGps(mx, my, anchor)))),
        };
    }
    return null;
};

const getSavedSlamPlacement = (area?: Area | null): SlamPlacementLike | null => {
    const saved = area?.configurations?.slamPlacement;
    if (saved?.lat == null || saved?.lng == null) return null;
    return {
        lat: Number(saved.lat),
        lng: Number(saved.lng),
        bearingDegrees: Number(saved.bearingDegrees ?? 0),
    };
};

const getGpsGeometryForAreaMap = (area: Area | null | undefined, selectedArea: Area | null): any | null => {
    if (!area?.geom) return null;
    if (area.spaceType === 'indoor') {
        const anchor = getSavedSlamPlacement(area) || getSavedSlamPlacement(selectedArea);
        return anchor ? convertLocalGeometryToGps(area.geom, anchor) : null;
    }
    return isGpsGeometry(area.geom) ? area.geom : null;
};

interface DashboardNavigationState {
    areaId?: number;
    metricKey?: string;
    metricId?: number;
    measurementId?: number;
    patternId?: number;
    patternPointId?: number;
    dateFrom?: string;
    dateTo?: string;
}

const findAreaInTree = (areaId: number, areaList: Area[]): Area | null => {
    for (const area of areaList) {
        if (area.id === areaId) return area;
        if (area.children && area.children.length > 0) {
            const found = findAreaInTree(areaId, area.children);
            if (found) return found;
        }
    }
    return null;
};

const findAreaPath = (areaId: number, areaList: Area[], path: number[] = []): number[] => {
    for (const area of areaList) {
        const nextPath = [...path, area.id];
        if (area.id === areaId) return nextPath;
        if (area.children && area.children.length > 0) {
            const found = findAreaPath(areaId, area.children, nextPath);
            if (found.length > 0) return found;
        }
    }
    return [];
};

const collectDescendants = (area: Area): Area[] => {
    const result: Area[] = [];
    const walk = (node: Area) => {
        if (!node.children || node.children.length === 0) return;
        node.children.forEach((child) => {
            result.push(child);
            walk(child);
        });
    };
    walk(area);
    return result;
};

const createDefaultDateRange = (): [Dayjs, Dayjs] => [
    dayjs().subtract(29, 'day'),
    dayjs()
];

const SHARE_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Reports: React.FC = () => {
    const location = useLocation();
    const { currentUser } = useAuth();
    const canShareReports = useRoleControl(['admin']);
    const [loading, setLoading] = useState(true);
    const [showArchivedAreas, setShowArchivedAreas] = useState(false);
    const [areas, setAreas] = useState<Area[]>([]);
    const [selectedArea, setSelectedArea] = useState<Area | null>(null);
    const [templates, setTemplates] = useState<ReportTemplate[]>([]);
    const [selectedTemplate, setSelectedTemplate] = useState<ReportTemplate | null>(null);
    const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>(() => createDefaultDateRange());
    const [showTemplateSelector, setShowTemplateSelector] = useState(false);
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [pendingMeasurementId, setPendingMeasurementId] = useState<number | null>(null);
    const [showSaveReportModal, setShowSaveReportModal] = useState(false);
    const [savingReport, setSavingReport] = useState(false);
    const [saveReportForm] = Form.useForm();
    const [companyUsers, setCompanyUsers] = useState<Array<{ id: number; email: string }>>([]);
    const [loadingCompanyUsers, setLoadingCompanyUsers] = useState(false);
    const [currentReport, setCurrentReport] = useState<ReportTemplateState | null>(null);
    const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
    const [viewState, setViewState] = useState({ longitude: -0.1278, latitude: 51.5074, zoom: 13, bearing: 0, pitch: 0 });
    const [areaTypeTemplates, setAreaTypeTemplates] = useState<Map<number, string>>(new Map());
    const [checkedAreaTypes, setCheckedAreaTypes] = useState<Set<number>>(new Set());
    const [selectedAreaDetail, setSelectedAreaDetail] = useState<any>(null);
    const reportViewKeyRef = useRef<string | null>(null);
    const pendingDashboardNavRef = useRef<DashboardNavigationState | null>(null);
    const consumedDashboardNavKeysRef = useRef<Set<string>>(new Set());
    const reportsNavKeyRef = useRef<string | null>(null);
    const [navLoading, setNavLoading] = useState(false);
    const [areaSearch, setAreaSearch] = useState('');
    const [expandedStatusCards, setExpandedStatusCards] = useState<Set<HealthStatus>>(new Set());
    const autoSelectedRef = useRef(false);
    const autoExpandedRef = useRef(false);
    const screens = useBreakpoint();
    const isMobile = screens.md === false;
    
    // Navigation history for back button
    const [navigationHistory, setNavigationHistory] = useState<NavigationState[]>([]);

    // Browser history sync for back button support
    const navigate = useNavigate();
    const isProgrammaticNavRef = useRef(false);
    const navHistoryLengthRef = useRef(0);

    const activeCompanyId = currentUser?.currentCompanyId || currentUser?.companyId || null;

    const chatAreaId = currentReport?.areaId ?? null;
    const chatAreaName = currentReport ? selectedArea?.name ?? null : null;
    const chatDateRange = currentReport ? dateRange : null;
    const chatMeasurementId = currentReport?.measurementId ?? null;
    const chatPatternId = currentReport?.patternId ?? null;
    const chatPatternPointId = currentReport?.patternPointId ?? null;

    // Keep navHistoryLengthRef in sync with navigationHistory state
    useEffect(() => {
        navHistoryLengthRef.current = navigationHistory.length;
    }, [navigationHistory]);

    useEffect(() => {
        trackActivity({
            action: 'view',
            entityType: 'page',
            route: window.location.pathname,
        });
    }, []);

    useEffect(() => {
        const state = (location.state as { dashboardNav?: DashboardNavigationState } | null) || null;
        if (state?.dashboardNav && !consumedDashboardNavKeysRef.current.has(location.key)) {
            consumedDashboardNavKeysRef.current.add(location.key);
            pendingDashboardNavRef.current = state.dashboardNav;
            setNavLoading(true);
            
            // Safety timeout to prevent infinite loading if something goes wrong
            const timeout = setTimeout(() => {
                console.warn('[Reports] Navigation loading timeout - forcing navLoading to false');
                setNavLoading(false);
                message.error('Report loading timed out. Please try again.');
            }, 15000); // 15 second timeout
            
            return () => clearTimeout(timeout);
        }
    }, [location.key, location.state]);

    useEffect(() => {
        if (!showSaveReportModal || !canShareReports) return;
        let mounted = true;
        setLoadingCompanyUsers(true);
        axios
            .get(ENDPOINTS.GET_COMPANY_USERS, { withCredentials: true })
            .then((res) => {
                if (!mounted) return;
                const payload = res.data?.data ?? res.data ?? [];
                setCompanyUsers(Array.isArray(payload) ? payload : []);
            })
            .catch((err) => {
                console.warn('Failed to load company users:', err);
                if (mounted) setCompanyUsers([]);
            })
            .finally(() => {
                if (mounted) setLoadingCompanyUsers(false);
            });
        return () => {
            mounted = false;
        };
    }, [showSaveReportModal, canShareReports]);

    const fetchAreaDetail = useCallback(async (id: number, currentAreas?: Area[]) => {
        try {
            const res = await axios.get(ENDPOINTS.AREA_DETAIL(id), { withCredentials: true });
            const payload = res.data?.data || res.data;
            if (payload) {
                const areaInTree = findAreaInTree(id, currentAreas || areas);
                if (areaInTree) {
                    setSelectedAreaDetail({ ...payload, children: areaInTree.children });
                } else {
                    setSelectedAreaDetail(payload);
                }
            }
        } catch (error) {
            console.warn('Failed to load area detail', error);
        }
    }, [areas]);

    const fetchAreas = useCallback(async () => {
        console.log('[Reports] 📍 fetchAreas called, activeCompanyId:', activeCompanyId);
        setLoading(true);
        try {
            const res = await axios.get(ENDPOINTS.AREAS_V2, { withCredentials: true, params: { include_archived: showArchivedAreas ? 'true' : undefined } });
            const payload = res.data?.data || res.data || [];
            console.log('[Reports] ✅ Areas fetched from API:', payload.length);
            
            // Don't filter by company if we have pending dashboard navigation
            // This ensures measurement navigation works even across company boundaries
            const shouldFilterByCompany = activeCompanyId && !pendingDashboardNavRef.current;
            const scopedPayload = shouldFilterByCompany
                ? payload.filter((item: Area) => item.companyId === activeCompanyId)
                : payload;
            console.log('[Reports] 📊 Areas after company filter:', { shouldFilter: shouldFilterByCompany, count: scopedPayload.length });
            
            // Build tree structure
            const buildTree = (items: Area[]): Area[] => {
                const map: { [key: number]: Area } = {};
                const roots: Area[] = [];
                
                items.forEach(item => {
                    map[item.id] = { ...item, children: [] };
                });
                
                items.forEach(item => {
                    if (item.parentAreaId && map[item.parentAreaId]) {
                        map[item.parentAreaId].children!.push(map[item.id]);
                    } else {
                        roots.push(map[item.id]);
                    }
                });
                
                return roots;
            };
            
            const tree = buildTree(scopedPayload);
            const annotateDepth = (items: Area[], depth: number = 0): Area[] =>
                items.map((item) => ({
                    ...item,
                    hierarchyDepth: depth,
                    children: item.children ? annotateDepth(item.children, depth + 1) : [],
                }));
            const treeWithDepth = annotateDepth(tree);
            setAreas(treeWithDepth);
            if (treeWithDepth.length > 0 && !pendingDashboardNavRef.current) {
                setSelectedArea(treeWithDepth[0]);
                fetchAreaDetail(treeWithDepth[0].id, treeWithDepth);
                setExpandedRowKeys([String(treeWithDepth[0].id)]);
            }
        } catch (error) {
            console.error('Failed to load areas', error);
            message.error('Could not load areas.');
        } finally {
            setLoading(false);
        }
    }, [activeCompanyId, showArchivedAreas]);

    useEffect(() => {
        fetchAreas();
    }, [fetchAreas]);

    useEffect(() => {
        if (!currentReport?.template) return;
        const templateId = (currentReport.template as any)?.id ?? null;
        const key = [
            templateId ?? 'none',
            currentReport.areaId ?? 'none',
            currentReport.dateFrom ? new Date(currentReport.dateFrom).toISOString() : '',
            currentReport.dateTo ? new Date(currentReport.dateTo).toISOString() : '',
            currentReport.measurementId ?? 'none',
        ].join('|');

        if (reportViewKeyRef.current === key) return;
        reportViewKeyRef.current = key;

        trackActivity({
            action: 'view',
            entityType: 'report',
            entityId: templateId ?? undefined,
            reportTemplateId: templateId ?? undefined,
            areaId: currentReport.areaId ?? null,
            measurementId: currentReport.measurementId ?? null,
            dateFrom: currentReport.dateFrom,
            dateTo: currentReport.dateTo,
            route: window.location.pathname,
        });
    }, [currentReport]);

    const handleGenerateReport = useCallback(async (
        area: Area,
        saveHistory: boolean = false,
        rangeOverride?: [Dayjs, Dayjs]
    ) => {
        const activeRange = rangeOverride ?? dateRange;
        trackActivity({
            action: 'select',
            entityType: 'area',
            entityId: area.id,
            areaId: area.id,
            reportTemplateId: currentReport?.template?.id,
            dateFrom: activeRange[0].toDate(),
            dateTo: activeRange[1].toDate(),
            route: window.location.pathname,
        });
        setPendingMeasurementId(null);

        // Save current state to history before navigating
        if (saveHistory && currentReport && selectedArea) {
            setNavigationHistory(prev => [...prev, {
                area: selectedArea,
                template: currentReport.template,
                dateRange: [dayjs(currentReport.dateFrom), dayjs(currentReport.dateTo)],
                patternId: currentReport.patternId ?? null,
                patternPointId: currentReport.patternPointId ?? null,
                measurementId: currentReport.measurementId ?? null,
            }]);
            // Push browser history entry so browser back button navigates within reports
            isProgrammaticNavRef.current = true;
            navigate('/reports', { state: { _reportNav: true } });
        }

        setSelectedArea(area);
        setDateRange(activeRange);
        setCurrentReport(null);
        
        if (!area.areaType?.id) {
            message.warning('This area has no area type assigned');
            return;
        }

        setLoadingTemplates(true);

        try {
            // Fetch templates for this area type with area navigation context
            const res = await axios.get(ENDPOINTS.REPORT_TEMPLATES, {
                params: { 
                    areaTypeId: area.areaType.id, 
                    status: 'active',
                    navigationContext: 'area'  // Only show templates accessible from area dropdown
                },
                withCredentials: true
            });
            
            const templatesData = res.data?.data || res.data || [];
            setTemplates(templatesData);

            if (templatesData.length === 0) {
                message.warning('No templates available for this area type');
                setLoadingTemplates(false);
                return;
            } else if (templatesData.length === 1) {
                // Auto-generate if only one template
                setLoadingTemplates(false);
                setCurrentReport({
                    template: templatesData[0],
                    areaId: area.id,
                    dateFrom: activeRange[0].toDate(),
                    dateTo: activeRange[1].toDate(),
                    patternId: null,
                    patternPointId: null,
                    measurementId: null,
                });
                return;
            } else {
                // Show selector if multiple templates
                setSelectedTemplate(null);
                setShowTemplateSelector(true);
            }
        } catch (err) {
            message.error('Failed to load templates');
        } finally {
            setLoadingTemplates(false);
        }
    }, [currentReport, dateRange, selectedArea]);

    // Handle area click from report sections
    const handleAreaClick = useCallback(async (areaId: number, rangeOverride?: [Dayjs, Dayjs]) => {
        const activeRange = rangeOverride ?? dateRange;
        // Find the area in the tree
        const area = findAreaInTree(areaId, areas);
        if (!area) {
            message.error('Area not found');
            return;
        }

        // Fetch area detail
        try {
            const res = await axios.get(ENDPOINTS.AREA_DETAIL(areaId), { withCredentials: true });
            const areaDetail = res.data?.data || res.data;
            
            // Merge with tree structure
            const areaInTree = findAreaInTree(areaId, areas);
            const fullArea = areaInTree 
                ? { ...areaDetail, children: areaInTree.children }
                : areaDetail;

            trackActivity({
                action: 'select',
                entityType: 'area',
                entityId: fullArea.id,
                areaId: fullArea.id,
                reportTemplateId: currentReport?.template?.id,
                dateFrom: activeRange[0].toDate(),
                dateTo: activeRange[1].toDate(),
                route: window.location.pathname,
            });

            const templateAreaTypeIds =
                currentReport?.template?.areaTypeIds ??
                currentReport?.template?.metadata?.appliesToAreaTypes ??
                [];
            const templateSupportsArea =
                !templateAreaTypeIds?.length ||
                templateAreaTypeIds.includes(fullArea.areaType?.id);

            if (currentReport && templateSupportsArea) {
                if (selectedArea) {
                    setNavigationHistory(prev => [...prev, {
                        area: selectedArea,
                        template: currentReport.template,
                        dateRange: [dayjs(currentReport.dateFrom), dayjs(currentReport.dateTo)],
                        patternId: currentReport.patternId ?? null,
                        patternPointId: currentReport.patternPointId ?? null,
                        measurementId: currentReport.measurementId ?? null,
                    }]);
                    // Push browser history entry so browser back button navigates within reports
                    isProgrammaticNavRef.current = true;
                    navigate('/reports', { state: { _reportNav: true } });
                }
                setDateRange(activeRange);
                setSelectedArea(fullArea);
                setCurrentReport((prev: ReportTemplateState | null) =>
                    prev
                        ? {
                              ...prev,
                              areaId: fullArea.id,
                              dateFrom: activeRange[0].toDate(),
                              dateTo: activeRange[1].toDate(),
                          }
                        : prev
                );
                window.scrollTo({ top: 0, behavior: 'smooth' });
                return;
            }

            // Generate report with history if template changes
            await handleGenerateReport(fullArea, true, activeRange);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (error) {
            console.error('Failed to navigate to area:', error);
            message.error('Failed to load area details');
        }
    }, [areas, currentReport, dateRange, handleGenerateReport, selectedArea]);

    // Handle metric click from report sections
    const handleMetricClick = useCallback(async (
        metricKey: string,
        metricId: number | undefined,
        areaId: number,
        rangeOverride?: [Dayjs, Dayjs]
    ) => {
        const activeRange = rangeOverride ?? dateRange;
        console.log('🎯 Metric clicked:', { metricKey, metricId, areaId });
        setPendingMeasurementId(null);
        
        // Find the area in the tree
        const area = findAreaInTree(areaId, areas);
        if (!area || !area.areaType?.id) {
            message.error('Invalid area or area type');
            return;
        }

        // Need metric ID for filtering
        if (!metricId) {
            message.warning('Metric ID not available for filtering');
            return;
        }

        trackActivity({
            action: 'select',
            entityType: 'metric',
            entityId: metricId,
            metricId,
            areaId,
            reportTemplateId: currentReport?.template?.id,
            dateFrom: activeRange[0].toDate(),
            dateTo: activeRange[1].toDate(),
            route: window.location.pathname,
        });

        setLoadingTemplates(true);

        try {
            // Fetch templates filtered by BOTH metric AND area type with metric navigation context
            const res = await axios.get(ENDPOINTS.REPORT_TEMPLATES, {
                params: {
                    metricId: metricId,
                    areaTypeId: area.areaType.id,
                    status: 'active',
                    navigationContext: 'metric'  // Only show templates accessible from metric links
                },
                withCredentials: true
            });

            const templatesData = res.data?.data || [];

            if (templatesData.length === 0) {
                message.info(`No templates found for metric "${metricKey}" and area type "${area.areaType.name}"`);
                setLoadingTemplates(false);
                return;
            }

            // Save current state to history before navigating
            if (currentReport && selectedArea) {
                setNavigationHistory(prev => [...prev, {
                    area: selectedArea,
                    template: currentReport.template,
                    dateRange: [dayjs(currentReport.dateFrom), dayjs(currentReport.dateTo)],
                    patternId: currentReport.patternId ?? null,
                    patternPointId: currentReport.patternPointId ?? null,
                    measurementId: currentReport.measurementId ?? null,
                }]);
                // Push browser history entry so browser back button navigates within reports
                isProgrammaticNavRef.current = true;
                navigate('/reports', { state: { _reportNav: true } });
            }

            setSelectedArea(area);
            setTemplates(templatesData);

            if (templatesData.length === 1) {
                // Auto-load single template
                setLoadingTemplates(false);
                setCurrentReport({
                    template: templatesData[0],
                    areaId: area.id,
                    dateFrom: activeRange[0].toDate(),
                    dateTo: activeRange[1].toDate(),
                    patternId: null,
                    patternPointId: null,
                    measurementId: null,
                });
                window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
                // Show selector for multiple templates
                setSelectedTemplate(null);
                setShowTemplateSelector(true);
                setLoadingTemplates(false);
            }
        } catch (err) {
            console.error('Failed to load templates for metric:', err);
            message.error('Failed to load templates for metric');
            setLoadingTemplates(false);
        }
    }, [areas, currentReport, selectedArea, dateRange]);

    const handleMeasurementClick = useCallback(async (
        measurementId: number,
        areaId?: number | null,
        rangeOverride?: [Dayjs, Dayjs]
    ) => {
        console.log('[Reports] 🔍 handleMeasurementClick START:', { measurementId, areaId, rangeOverride });
        const activeRange = rangeOverride ?? dateRange;
        let targetArea: Area | null = null;
        let hasArea = false;

        console.log('[Reports] 📡 Fetching measurement detail for ID:', measurementId);
        try {
            const measurementRes = await axios.get(ENDPOINTS.MEASUREMENT_DETAIL(measurementId), {
                withCredentials: true,
            });
            const measurement = measurementRes.data?.data || measurementRes.data;
            console.log('[Reports] ✅ Measurement detail fetched:', measurement);
            const measurementAreaId = measurement?.area?.id ?? measurement?.areaId ?? null;
            console.log('[Reports] 🏠 Measurement area ID:', measurementAreaId);

            if (measurementAreaId) {
                targetArea = findAreaInTree(measurementAreaId, areas);
                if (!targetArea) {
                    if (measurement?.area?.id) {
                        targetArea = {
                            id: measurement.area.id,
                            name: measurement.area.name,
                            areaType: measurement.area.areaType ?? measurement.areaType,
                            parentAreaId: measurement.area.parentAreaId ?? null,
                            children: [],
                        };
                    } else {
                        const areaRes = await axios.get(ENDPOINTS.AREA_DETAIL(measurementAreaId), {
                            withCredentials: true,
                        });
                        const areaPayload = areaRes.data?.data || areaRes.data;
                        if (areaPayload?.id) {
                            targetArea = {
                                id: areaPayload.id,
                                name: areaPayload.name,
                                areaType: areaPayload.areaType,
                                parentAreaId: areaPayload.parentAreaId ?? null,
                                children: [],
                            };
                        }
                    }
                }
            }
        } catch (err) {
            console.warn('Failed to resolve measurement area', err);
        }

        if (!targetArea && areaId) {
            targetArea = findAreaInTree(areaId, areas);
            if (!targetArea) {
                try {
                    const areaRes = await axios.get(ENDPOINTS.AREA_DETAIL(areaId), {
                        withCredentials: true,
                    });
                    const areaPayload = areaRes.data?.data || areaRes.data;
                    if (areaPayload?.id) {
                        targetArea = {
                            id: areaPayload.id,
                            name: areaPayload.name,
                            areaType: areaPayload.areaType,
                            parentAreaId: areaPayload.parentAreaId ?? null,
                            children: [],
                        };
                    }
                } catch (err) {
                    console.warn('Failed to resolve fallback area', err);
                }
            }
        }

        hasArea = Boolean(targetArea && targetArea.areaType?.id);
        console.log('[Reports] 🎯 Area resolution complete:', { hasArea, targetArea: targetArea?.name, areaTypeId: targetArea?.areaType?.id });

        setSelectedArea(hasArea ? targetArea : null);
        if (!hasArea) {
            setSelectedAreaDetail(null);
        }

        trackActivity({
            action: 'select',
            entityType: 'measurement',
            entityId: measurementId,
            measurementId,
            areaId: hasArea ? targetArea!.id : null,
            reportTemplateId: currentReport?.template?.id,
            dateFrom: activeRange[0].toDate(),
            dateTo: activeRange[1].toDate(),
            route: window.location.pathname,
        });

        console.log('[Reports] 🔄 setLoadingTemplates(true)');
        setLoadingTemplates(true);
        setPendingMeasurementId(null);

        const templateParams = {
            measurementId,
            status: 'active',
            navigationContext: 'measurement',
            ...(hasArea ? { areaTypeId: targetArea!.areaType!.id } : {}),
        };
        console.log('[Reports] 📡 Fetching templates with params:', templateParams);
        console.log('[Reports] 📍 API Endpoint:', ENDPOINTS.REPORT_TEMPLATES);

        try {
            const res = await axios.get(ENDPOINTS.REPORT_TEMPLATES, {
                params: templateParams,
                withCredentials: true,
            });

            console.log('[Reports] ✅ Templates API response:', res.data);
            const templatesData = res.data?.data || [];
            console.log('[Reports] 📊 Templates extracted:', { count: templatesData.length, templates: templatesData });

            if (templatesData.length === 0) {
                console.log('[Reports] ⚠️ No templates found - showing warning');
                if (hasArea) {
                    message.warning(`No report templates available for this measurement and area type "${targetArea!.areaType!.name}". Please create a measurement template or assign this measurement to an area with an available template.`);
                } else {
                    message.warning('No report templates available for this measurement. This measurement is not assigned to an area. Please either:\n1. Create a measurement-specific template, or\n2. Assign this measurement to an area first.');
                }
                setLoadingTemplates(false);
                return;
            }

            if (currentReport && selectedArea) {
                setNavigationHistory(prev => [...prev, {
                    area: selectedArea,
                    template: currentReport.template,
                    dateRange: [dayjs(currentReport.dateFrom), dayjs(currentReport.dateTo)],
                    patternId: currentReport.patternId ?? null,
                    patternPointId: currentReport.patternPointId ?? null,
                    measurementId: currentReport.measurementId ?? null,
                }]);
                // Push browser history entry so browser back button navigates within reports
                isProgrammaticNavRef.current = true;
                navigate('/reports', { state: { _reportNav: true } });
            }

            setTemplates(templatesData);

            if (templatesData.length === 1) {
                setLoadingTemplates(false);
                setCurrentReport({
                    template: templatesData[0],
                    areaId: hasArea ? targetArea!.id : null,
                    dateFrom: activeRange[0].toDate(),
                    dateTo: activeRange[1].toDate(),
                    patternId: null,
                    patternPointId: null,
                    measurementId,
                });
                window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
                setSelectedTemplate(null);
                setPendingMeasurementId(measurementId);
                setShowTemplateSelector(true);
                setLoadingTemplates(false);
            }
        } catch (err) {
            console.error('Failed to load templates for measurement:', err);
            message.error('Failed to load report templates. Please try again or contact support if the issue persists.');
            setLoadingTemplates(false);
        }
    }, [areas, currentReport, dateRange, selectedArea]);

    // Handle pattern click from report sections
    const handlePatternClick = useCallback(async (
        patternId: number,
        areaId: number,
        rangeOverride?: [Dayjs, Dayjs]
    ) => {
        const activeRange = rangeOverride ?? dateRange;
        console.log('🎨 Pattern clicked:', { patternId, areaId });
        setPendingMeasurementId(null);
        
        // Find the area in the tree
        const area = findAreaInTree(areaId, areas);
        if (!area || !area.areaType?.id) {
            message.error('Invalid area or area type');
            return;
        }

        trackActivity({
            action: 'select',
            entityType: 'pattern',
            entityId: patternId,
            patternId,
            areaId,
            reportTemplateId: currentReport?.template?.id,
            dateFrom: activeRange[0].toDate(),
            dateTo: activeRange[1].toDate(),
            route: window.location.pathname,
        });

        setLoadingTemplates(true);

        try {
            // Fetch templates filtered by BOTH pattern AND area type with pattern navigation context
            const res = await axios.get(ENDPOINTS.REPORT_TEMPLATES, {
                params: {
                    patternId: patternId,
                    areaTypeId: area.areaType.id,
                    status: 'active',
                    navigationContext: 'pattern'  // Only show templates accessible from pattern links
                },
                withCredentials: true
            });

            const templatesData = res.data?.data || [];

            if (templatesData.length === 0) {
                message.info(`No templates found for this pattern and area type "${area.areaType.name}"`);
                setLoadingTemplates(false);
                return;
            }

            // Save current state to history before navigating
            if (currentReport && selectedArea) {
                setNavigationHistory(prev => [...prev, {
                    area: selectedArea,
                    template: currentReport.template,
                    dateRange: [dayjs(currentReport.dateFrom), dayjs(currentReport.dateTo)],
                    patternId: currentReport.patternId ?? null,
                    patternPointId: currentReport.patternPointId ?? null,
                    measurementId: currentReport.measurementId ?? null,
                }]);
                // Push browser history entry so browser back button navigates within reports
                isProgrammaticNavRef.current = true;
                navigate('/reports', { state: { _reportNav: true } });
            }

            setSelectedArea(area);
            setDateRange(activeRange);
            setTemplates(templatesData);

            if (templatesData.length === 1) {
                // Auto-load single template
                setLoadingTemplates(false);
                setCurrentReport({
                    template: templatesData[0],
                    areaId: area.id,
                    dateFrom: activeRange[0].toDate(),
                    dateTo: activeRange[1].toDate(),
                    patternId: patternId,
                    patternPointId: null,
                    measurementId: null,
                });
                window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
                // Show selector for multiple templates
                setSelectedTemplate(null);
                setShowTemplateSelector(true);
                setLoadingTemplates(false);
            }
        } catch (err) {
            console.error('Failed to load templates for pattern:', err);
            message.error('Failed to load templates for pattern');
            setLoadingTemplates(false);
        }
    }, [areas, currentReport, selectedArea, dateRange, navigate]);

    // Handle pattern point click from report sections
    const handlePatternPointClick = useCallback(async (
        patternPointId: number,
        patternId: number,
        areaId: number,
        rangeOverride?: [Dayjs, Dayjs]
    ) => {
        const activeRange = rangeOverride ?? dateRange;
        console.log('🔵 Pattern Point clicked:', { patternPointId, patternId, areaId });
        setPendingMeasurementId(null);
        
        // Find the area in the tree
        const area = findAreaInTree(areaId, areas);
        if (!area || !area.areaType?.id) {
            message.error('Invalid area or area type');
            return;
        }

        trackActivity({
            action: 'select',
            entityType: 'pattern_point',
            entityId: patternPointId,
            patternId,
            patternPointId,
            areaId,
            reportTemplateId: currentReport?.template?.id,
            dateFrom: activeRange[0].toDate(),
            dateTo: activeRange[1].toDate(),
            route: window.location.pathname,
        });

        setLoadingTemplates(true);

        try {
            // Fetch templates filtered by pattern point, pattern, AND area type with pattern point navigation context
            const res = await axios.get(ENDPOINTS.REPORT_TEMPLATES, {
                params: {
                    patternPointId: patternPointId,
                    patternId: patternId,  // Include parent pattern for hierarchical context
                    areaTypeId: area.areaType.id,
                    status: 'active',
                    navigationContext: 'point'  // Only show templates accessible from pattern point links
                },
                withCredentials: true
            });

            const templatesData = res.data?.data || [];

            if (templatesData.length === 0) {
                message.info(`No templates found for this pattern point and area type "${area.areaType.name}"`);
                setLoadingTemplates(false);
                return;
            }

            // Save current state to history before navigating
            if (currentReport && selectedArea) {
                setNavigationHistory(prev => [...prev, {
                    area: selectedArea,
                    template: currentReport.template,
                    dateRange: [dayjs(currentReport.dateFrom), dayjs(currentReport.dateTo)],
                    patternId: currentReport.patternId ?? null,
                    patternPointId: currentReport.patternPointId ?? null,
                    measurementId: currentReport.measurementId ?? null,
                }]);
                // Push browser history entry so browser back button navigates within reports
                isProgrammaticNavRef.current = true;
                navigate('/reports', { state: { _reportNav: true } });
            }

            setSelectedArea(area);
            setDateRange(activeRange);
            setTemplates(templatesData);

            if (templatesData.length === 1) {
                // Auto-load single template
                setLoadingTemplates(false);
                setCurrentReport({
                    template: templatesData[0],
                    areaId: area.id,
                    dateFrom: activeRange[0].toDate(),
                    dateTo: activeRange[1].toDate(),
                    patternId: patternId,
                    patternPointId: patternPointId,
                    measurementId: null,
                });
                window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
                // Show selector for multiple templates
                setSelectedTemplate(null);
                setShowTemplateSelector(true);
                setLoadingTemplates(false);
            }
        } catch (err) {
            console.error('Failed to load templates for pattern point:', err);
            message.error('Failed to load templates for pattern point');
            setLoadingTemplates(false);
        }
    }, [areas, currentReport, selectedArea, dateRange, navigate]);

    useEffect(() => {
        const nav = pendingDashboardNavRef.current;
        console.log('[Reports] 🚀 Dashboard navigation useEffect triggered:', { nav, areasCount: areas.length });
        if (!nav) {
            console.log('[Reports] ⏭️ Skipping navigation - no nav');
            return;
        }
        
        // Only require areas for area-dependent navigation
        // Measurements can load without areas (measurement-specific templates)
        const isMeasurementOnly = nav.measurementId && !nav.areaId && !nav.patternId && !nav.patternPointId && !nav.metricId;
        const needsAreas = !isMeasurementOnly;
        
        if (needsAreas && areas.length === 0) {
            console.log('[Reports] ⏭️ Skipping area-dependent navigation - no areas loaded yet');
            return;
        }
        console.log('[Reports] ✅ Processing dashboard navigation:', { nav, isMeasurementOnly, needsAreas });
        pendingDashboardNavRef.current = null;
        autoSelectedRef.current = true;

        const rangeOverride = nav.dateFrom && nav.dateTo
            ? ([dayjs(nav.dateFrom), dayjs(nav.dateTo)] as [Dayjs, Dayjs])
            : undefined;

        if (rangeOverride) {
            console.log('[Reports] 📅 Setting date range override:', rangeOverride);
            setDateRange(rangeOverride);
        }

        const run = async () => {
            console.log('[Reports] 🔀 Routing navigation based on type...');
            if (nav.patternPointId && nav.patternId && nav.areaId) {
                console.log('[Reports] → Pattern Point navigation');
                await handlePatternPointClick(nav.patternPointId, nav.patternId, nav.areaId, rangeOverride);
                return;
            }

            if (nav.patternId && nav.areaId) {
                console.log('[Reports] → Pattern navigation');
                await handlePatternClick(nav.patternId, nav.areaId, rangeOverride);
                return;
            }

            if (nav.measurementId) {
                console.log('[Reports] → Measurement navigation:', { measurementId: nav.measurementId, areaId: nav.areaId });
                await handleMeasurementClick(nav.measurementId, nav.areaId, rangeOverride);
                return;
            }

            if (nav.metricId && nav.areaId) {
                console.log('[Reports] → Metric navigation');
                await handleMetricClick(nav.metricKey || 'metric', nav.metricId, nav.areaId, rangeOverride);
                return;
            }

            if (nav.areaId) {
                console.log('[Reports] → Area navigation');
                await handleAreaClick(nav.areaId, rangeOverride);
            }
        };

        run()
            .catch((error) => {
                console.error('[Reports] ❌ Navigation error:', error);
                // handlers already surface user-facing errors
            })
            .finally(() => {
                console.log('[Reports] 🏁 Navigation complete - setNavLoading(false)');
                setNavLoading(false);
            });
    }, [areas, handleAreaClick, handleMeasurementClick, handleMetricClick, handlePatternClick, handlePatternPointClick]);

    // Handle back navigation
    // fromBrowser=true when triggered by browser back button (history already changed)
    const handleBack = useCallback((fromBrowser = false) => {
        if (navigationHistory.length === 0) return;

        const previousState = navigationHistory[navigationHistory.length - 1];
        setNavigationHistory(prev => prev.slice(0, -1));

        // Restore previous state
        setSelectedArea(previousState.area);
        setDateRange(previousState.dateRange);
        setCurrentReport({
            template: previousState.template,
            areaId: previousState.area.id,
            dateFrom: previousState.dateRange[0].toDate(),
            dateTo: previousState.dateRange[1].toDate(),
            patternId: previousState.patternId ?? null,
            patternPointId: previousState.patternPointId ?? null,
            measurementId: previousState.measurementId ?? null,
        });

        // When using the in-app Back button, also pop browser history to keep in sync
        if (!fromBrowser) {
            isProgrammaticNavRef.current = true;
            navigate(-1);
        }

        // Scroll to top
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [navigationHistory, navigate]);

    const resetReportView = useCallback(() => {
        pendingDashboardNavRef.current = null;
        setNavigationHistory([]);
        setCurrentReport(null);
        setSelectedArea(null);
        setSelectedAreaDetail(null);
        setTemplates([]);
        setSelectedTemplate(null);
        setShowTemplateSelector(false);
        setLoadingTemplates(false);
        setPendingMeasurementId(null);
        setDateRange(createDefaultDateRange());
        setAreaSearch('');
        setExpandedRowKeys([]);
    }, []);

    // Handle home navigation (clear all history)
    const handleHome = useCallback(() => {
        resetReportView();
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [resetReportView]);

    useEffect(() => {
        if (location.pathname !== '/reports') return;
        if (!reportsNavKeyRef.current) {
            reportsNavKeyRef.current = location.key;
            return;
        }
        if (reportsNavKeyRef.current === location.key) return;

        reportsNavKeyRef.current = location.key;

        // If we triggered this navigation programmatically (forward push or in-app back), skip
        if (isProgrammaticNavRef.current) {
            isProgrammaticNavRef.current = false;
            return;
        }

        // Browser back/forward or fresh navigation to /reports
        if (navHistoryLengthRef.current > 0) {
            // The destination entry may be the original report link and therefore
            // not carry _reportNav. The in-memory stack is the source of truth.
            handleBack(true);
        } else {
            // Fresh navigation to /reports (e.g. from sidebar) or past the end of our stack
            handleHome();
        }
    }, [location.key, location.pathname, location.state, handleBack, handleHome]);

    const handleConfirmGenerate = (templateOverride?: ReportTemplate) => {
        const templateToUse = templateOverride ?? selectedTemplate;
        if (!templateToUse) {
            message.warning('Please select a template');
            return;
        }

        setShowTemplateSelector(false);
        const resolvedMeasurementId = pendingMeasurementId ?? null;
        setPendingMeasurementId(null);

        if (!selectedArea && !resolvedMeasurementId) {
            message.warning('Please select an area');
            return;
        }

        // Set up report to display
        setCurrentReport({
            template: templateToUse,
            areaId: selectedArea?.id ?? null,
            dateFrom: dateRange[0].toDate(),
            dateTo: dateRange[1].toDate(),
            patternId: null,
            patternPointId: null,
            measurementId: resolvedMeasurementId,
        });
    };

    const handleCloseSelector = () => {
        setShowTemplateSelector(false);
        setSelectedTemplate(null);
        setTemplates([]);
        setPendingMeasurementId(null);
    };

    const buildDefaultReportTitle = useCallback(() => {
        const templateName = currentReport?.template?.name || 'Report';
        const scopeName = selectedArea?.name
            || (currentReport?.areaId ? `Area ${currentReport.areaId}` : null)
            || (currentReport?.measurementId ? `Measurement ${currentReport.measurementId}` : 'Report');
        return `${templateName} · ${scopeName} · ${dateRange[0].format('YYYY-MM-DD')} → ${dateRange[1].format('YYYY-MM-DD')}`;
    }, [currentReport, dateRange, selectedArea]);

    const openSaveReportModal = useCallback(() => {
        if (!currentReport) {
            message.warning('No report to save');
            return;
        }
        saveReportForm.setFieldsValue({
            title: buildDefaultReportTitle(),
            shareUserIds: [],
            shareEmails: [],
        });
        setShowSaveReportModal(true);
    }, [buildDefaultReportTitle, currentReport, saveReportForm]);

    const handleSaveReport = useCallback(async () => {
        if (!currentReport) {
            message.warning('No report to save');
            return;
        }
        if (!currentReport.areaId) {
            message.warning('Saved reports require a valid area');
            return;
        }

        try {
            const values = await saveReportForm.validateFields();
            setSavingReport(true);

            const reportTypeRaw = currentReport.template?.key || currentReport.template?.name || 'report';
            const shareUserIds = canShareReports
                ? (values.shareUserIds || [])
                      .map((id: number) => Number(id))
                      .filter((id: number) => !Number.isNaN(id))
                : [];
            const shareEmails = canShareReports
                ? (values.shareEmails || [])
                      .map((email: string) => String(email).trim().toLowerCase())
                      .filter((email: string) => email.length > 0)
                : [];
            const payload: Record<string, any> = {
                title: String(values.title).trim(),
                reportType: String(reportTypeRaw).slice(0, 50),
                areaId: currentReport.areaId,
                dateFrom: currentReport.dateFrom,
                dateTo: currentReport.dateTo,
                templateId: currentReport.template?.id,
                templateKey: currentReport.template?.key,
                status: 'published',
            };

            if (shareUserIds.length > 0) {
                payload.shareWithUserIds = Array.from(new Set(shareUserIds));
            }
            if (shareEmails.length > 0) {
                payload.shareWithEmails = Array.from(new Set(shareEmails));
            }

            const response = await axios.post(ENDPOINTS.REPORTS, payload, { withCredentials: true });
            message.success('Report saved');
            const shareWarnings = response.data?.shareWarnings;
            if (Array.isArray(shareWarnings) && shareWarnings.length > 0) {
                message.warning(shareWarnings[0]);
            }
            setShowSaveReportModal(false);
            saveReportForm.resetFields();
        } catch (err: any) {
            if (err?.errorFields) {
                return;
            }
            console.error('Failed to save report:', err);
            message.error('Failed to save report');
        } finally {
            setSavingReport(false);
        }
    }, [canShareReports, currentReport, saveReportForm]);

    const flattenedAreas = useMemo(() => {
        const result: Area[] = [];
        const walk = (items: Area[]) => {
            items.forEach((item) => {
                result.push(item);
                if (item.children && item.children.length > 0) {
                    walk(item.children);
                }
            });
        };
        walk(areas);
        return result;
    }, [areas]);

    const summaryTargets = useMemo(
        () => flattenedAreas.map((area) => ({ id: area.id, name: area.name })),
        [flattenedAreas]
    );

    const latestSummaryState = useLatestMeasurementSummaries(summaryTargets, {
        enabled: summaryTargets.length > 0,
    });

    const healthAreaIds = useMemo(
        () => summaryTargets.map((area) => area.id),
        [summaryTargets]
    );

    const healthStatusState = useAreaHealthStatuses(healthAreaIds, {
        enabled: healthAreaIds.length > 0,
    });

    const summaryByAreaId = useMemo(() => {
        return latestSummaryState.summaries.reduce<Record<number, typeof latestSummaryState.summaries[number]>>((acc, summary) => {
            acc[summary.areaId] = summary;
            return acc;
        }, {});
    }, [latestSummaryState.summaries]);

    const healthStatusByAreaId = useMemo(() => {
        return healthStatusState.statuses.reduce<Record<number, HealthStatus>>((acc, summary) => {
            acc[summary.areaId] = summary.status;
            return acc;
        }, {});
    }, [healthStatusState.statuses]);

    const healthStatusDetailsByAreaId = useMemo(() => {
        return healthStatusState.statuses.reduce<Record<number, typeof healthStatusState.statuses[number]>>((acc, summary) => {
            acc[summary.areaId] = summary;
            return acc;
        }, {});
    }, [healthStatusState.statuses]);


    const areasByStatus = useMemo(() => {
        const query = areaSearch.trim().toLowerCase();
        const matchesSearch = (area: Area) => {
            if (!query) return true;
            const name = area.name?.toLowerCase() || '';
            const typeName = area.areaType?.name?.toLowerCase() || '';
            return name.includes(query) || typeName.includes(query);
        };

        const buildStatusList = (status: HealthStatus) => {
            const walk = (nodes: Area[], depth: number): Area[] => {
                const results: Area[] = [];
                nodes.forEach((node) => {
                    const nodeStatus = healthStatusByAreaId[node.id] || 'no-data';
                    const selfMatches = matchesSearch(node) && nodeStatus === status;
                    const childResults = node.children && node.children.length > 0
                        ? walk(node.children, depth + 1)
                        : [];
                    if (selfMatches || childResults.length > 0) {
                        results.push({
                            ...node,
                            hierarchyDepth: depth,
                            children: undefined,
                        });
                        results.push(...childResults);
                    }
                });
                return results;
            };
            return walk(areas, 0);
        };

        return {
            'action-needed': buildStatusList('action-needed'),
            attention: buildStatusList('attention'),
            healthy: buildStatusList('healthy'),
            'no-data': buildStatusList('no-data'),
        } as Record<HealthStatus, Area[]>;
    }, [areaSearch, areas, healthStatusByAreaId]);

    const statusCounts = useMemo(() => {
        if (healthStatusState.loading) {
            return {
                healthy: 0,
                attention: 0,
                'action-needed': 0,
                'no-data': 0,
            } as Record<HealthStatus, number>;
        }
        const counts: Record<HealthStatus, number> = {
            healthy: 0,
            attention: 0,
            'action-needed': 0,
            'no-data': 0,
        };
        flattenedAreas.forEach((area) => {
            const status = healthStatusByAreaId[area.id] || 'no-data';
            counts[status] += 1;
        });
        return counts;
    }, [flattenedAreas, healthStatusByAreaId, healthStatusState.loading]);

    const toggleStatusCard = useCallback((status: HealthStatus) => {
        setExpandedStatusCards((prev) => {
            const next = new Set(prev);
            if (next.has(status)) {
                next.delete(status);
            } else {
                next.add(status);
            }
            return next;
        });
    }, []);

    const areaById = useMemo(() => {
        const map = new Map<number, Area>();
        const walk = (nodes: Area[]) => {
            nodes.forEach((node) => {
                map.set(node.id, node);
                if (node.children && node.children.length > 0) {
                    walk(node.children);
                }
            });
        };
        walk(areas);
        return map;
    }, [areas]);

    const getDescendantIds = useCallback((areaId: number) => {
        const root = areaById.get(areaId);
        if (!root || !root.children || root.children.length === 0) return [] as number[];
        const ids: number[] = [];
        const walk = (node: Area) => {
            if (!node.children || node.children.length === 0) return;
            node.children.forEach((child) => {
                ids.push(child.id);
                walk(child);
            });
        };
        walk(root);
        return ids;
    }, [areaById]);

    const getPriorityRangeForArea = useCallback((areaId: number): [Dayjs, Dayjs] | null => {
        const descendantIds = getDescendantIds(areaId);
        const candidateIds = descendantIds.length > 0 ? descendantIds : [areaId];
        let minStart: Dayjs | null = null;
        let maxEnd: Dayjs | null = null;

        candidateIds.forEach((id) => {
            const summary = healthStatusDetailsByAreaId[id];
            if (!summary) return;
            if (summary.status !== 'action-needed' && summary.status !== 'attention') return;
            if (!summary.dayStart) return;
            const start = dayjs(summary.dayStart);
            const end = summary.dayEnd ? dayjs(summary.dayEnd) : start.endOf('day');
            if (!minStart || start.isBefore(minStart)) minStart = start;
            if (!maxEnd || end.isAfter(maxEnd)) maxEnd = end;
        });

        if (minStart && maxEnd) {
            return [minStart, maxEnd];
        }

        const fallback = healthStatusDetailsByAreaId[areaId];
        if (!fallback) return null;
        if (fallback.status !== 'action-needed' && fallback.status !== 'attention') return null;
        if (!fallback.dayStart) return null;
        const start = dayjs(fallback.dayStart);
        const end = fallback.dayEnd ? dayjs(fallback.dayEnd) : start.endOf('day');
        return [start, end];
    }, [getDescendantIds, healthStatusDetailsByAreaId]);

    const topStatus = useMemo(() => {
        const priority = ['action-needed', 'attention', 'healthy', 'no-data'] as HealthStatus[];
        return priority.find((status) => statusCounts[status] > 0) || null;
    }, [statusCounts]);

    useEffect(() => {
        if (healthStatusState.loading) {
            setExpandedStatusCards(new Set());
            autoExpandedRef.current = false;
            autoSelectedRef.current = false;
            return;
        }
        if (!topStatus) return;
        if (autoExpandedRef.current) return;
        autoExpandedRef.current = true;
        setExpandedStatusCards(new Set([topStatus]));
    }, [topStatus, healthStatusState.loading]);

    useEffect(() => {
        if (!topStatus || autoSelectedRef.current || currentReport || selectedArea || healthStatusState.loading || navLoading || pendingDashboardNavRef.current) return;
        const list = areasByStatus[topStatus];
        if (!list || list.length === 0) return;
        const firstArea = list[0];
        if (!firstArea) return;
        autoSelectedRef.current = true;
        fetchAreaDetail(firstArea.id, areas);
        setSelectedArea(firstArea);
        const path = findAreaPath(firstArea.id, areas);
        const pathKeys = path.map((id) => String(id));
        setExpandedRowKeys(pathKeys);
    }, [topStatus, areasByStatus, areas, currentReport, selectedArea, fetchAreaDetail, healthStatusState.loading, navLoading]);

    const areaPathMap = useMemo(() => {
        const map = new Map<number, string[]>();
        const walk = (nodes: Area[], path: string[] = []) => {
            nodes.forEach((node) => {
                const nextPath = [...path, node.name];
                map.set(node.id, nextPath);
                if (node.children && node.children.length > 0) {
                    walk(node.children, nextPath);
                }
            });
        };
        walk(areas);
        return map;
    }, [areas]);

    const columns = useMemo<ColumnsType<Area>>(() => [
        {
            title: 'Area Name',
            dataIndex: 'name',
            key: 'name',
            render: (text: string, record: Area) => {
                const depth = record.hierarchyDepth || 0;
                const status = healthStatusByAreaId[record.id];
                const statusMeta = status ? HEALTH_STATUS_CONFIG[status] : null;
                const path = areaPathMap.get(record.id) || [text];
                const typeName = record.areaType?.name || '—';
                return (
                    <div className="report-name-cell" style={{ paddingLeft: depth * 16 }}>
                        {depth > 0 && <span className="report-depth-marker" />}
                        {status && statusMeta && (
                            <span
                                className={`report-status-dot report-status-dot--${status}`}
                                title={statusMeta.label}
                                aria-label={`${statusMeta.label} status`}
                            />
                        )}
                        <span>{text}</span>
                        {record.archivedAt && <Tag>Archived</Tag>}
                        <span className="reports-area-type-inline">{typeName}</span>
                    </div>
                );
            },
        },
        {
            title: 'Type',
            dataIndex: ['areaType', 'name'],
            key: 'areaType',
            responsive: ['md'] as Breakpoint[],
            render: (_: any, record: Area) => record.areaType?.name || '—',
        },
        {
            title: 'Actions',
            key: 'actions',
            width: isMobile ? 64 : 140,
            align: 'right' as const,
            render: (_: any, record: Area) => {
                const isSelected = selectedArea?.id === record.id;
                if (!isSelected) {
                    return null;
                }
                const status = healthStatusByAreaId[record.id];
                const hasData = status && status !== 'no-data';
                return (
                    <Button
                        size={isMobile ? 'small' : 'middle'}
                        type={isSelected || hasData ? 'primary' : 'default'}
                        icon={<FileTextOutlined />}
                        className={`report-generate-button ${isSelected ? 'report-generate-selected' : ''} ${
                            hasData ? '' : 'report-generate-no-data'
                        }`}
                        onClick={(e) => {
                            e.stopPropagation();
                            const rangeOverride = getPriorityRangeForArea(record.id);
                            if (rangeOverride) {
                                setDateRange(rangeOverride);
                            }
                            handleGenerateReport(record, false, rangeOverride ?? undefined);
                        }}
                        aria-label={isSelected ? 'Generate report' : `Generate report for ${record.name}`}
                    >
                        {!isMobile && <span className="reports-view-button-text">View report</span>}
                    </Button>
                );
            },
        },
    ], [handleGenerateReport, selectedArea, healthStatusByAreaId, areaPathMap, getPriorityRangeForArea, isMobile]);

    // Update map view when area is selected
    useEffect(() => {
        const geom = getGpsGeometryForAreaMap(selectedAreaDetail, selectedAreaDetail);
        if (!geom?.coordinates?.length) return;
        if (geom.type === 'Point') {
            const [lng, lat] = geom.coordinates as number[];
            setViewState({ longitude: lng, latitude: lat, zoom: 15, bearing: 0, pitch: 0 });
        } else {
            const coords = geom.coordinates[0];
            if (Array.isArray(coords) && coords.length > 0) {
                const lon = coords.reduce((sum: number, c: any) => sum + c[0], 0) / coords.length;
                const lat = coords.reduce((sum: number, c: any) => sum + c[1], 0) / coords.length;
                setViewState({ longitude: lon, latitude: lat, zoom: 15, bearing: 0, pitch: 0 });
            }
        }
    }, [selectedAreaDetail]);

    // Collect all areas for map display
    const areasToDisplay = useMemo(() => {
        if (!selectedAreaDetail) return [];
        
        const collectChildren = (area: Area, depth: number = 0): Array<Area & { hierarchyDepth: number }> => {
            const result = [{ ...area, hierarchyDepth: depth }];
            if (area.children && area.children.length > 0) {
                area.children.forEach(child => {
                    result.push(...collectChildren(child, depth + 1));
                });
            }
            return result;
        };
        
        return collectChildren(selectedAreaDetail);
    }, [selectedAreaDetail]);

    const gpsAreasToDisplay = useMemo(() => (
        areasToDisplay
            .map((area) => {
                const geom = getGpsGeometryForAreaMap(area, selectedAreaDetail);
                return geom ? { ...area, geom } : null;
            })
            .filter((area): area is Area & { hierarchyDepth: number; geom: any } => !!area)
    ), [areasToDisplay, selectedAreaDetail]);

    const summaryAreas = useMemo(() => {
        const baseArea =
            selectedAreaDetail ??
            (selectedArea ? findAreaInTree(selectedArea.id, areas) : null);
        if (!baseArea) return [];
        const descendants = collectDescendants(baseArea);
        if (descendants.length > 0) {
            return descendants;
        }
        return [baseArea];
    }, [selectedAreaDetail, selectedArea, areas]);

    const latestSummariesForSelected = useMemo(() => {
        if (summaryAreas.length === 0) return [];
        return summaryAreas
            .map((area) => summaryByAreaId[area.id])
            .filter((summary): summary is NonNullable<typeof summary> => !!summary)
            .filter((summary) => !!summary.latestAt && summary.count > 0);
    }, [summaryAreas, summaryByAreaId]);

    const overviewStats = useMemo<{ counts: Record<HealthStatus, number>; lastScan: Dayjs | null }>(() => {
        const counts: Record<HealthStatus, number> = {
            healthy: 0,
            attention: 0,
            'action-needed': 0,
            'no-data': 0,
        };
        let lastScan: Dayjs | null = null;
        summaryAreas.forEach((area) => {
            const status = healthStatusByAreaId[area.id] || 'no-data';
            counts[status] += 1;
        });
        latestSummariesForSelected.forEach((summary) => {
            if (summary.latestAt && (!lastScan || summary.latestAt.isAfter(lastScan))) {
                lastScan = summary.latestAt;
            }
        });
        return { counts, lastScan };
    }, [healthStatusByAreaId, latestSummariesForSelected, summaryAreas]);

    // Load template images for area types
    useEffect(() => {
        if (!areasToDisplay || areasToDisplay.length === 0) return;

        (async () => {
            const newTypeIds = new Set<number>();
            areasToDisplay.forEach(area => {
                if (area.areaType?.id && !checkedAreaTypes.has(area.areaType.id)) {
                    newTypeIds.add(area.areaType.id);
                }
            });

            if (newTypeIds.size === 0) return;

            const updatedTemplateMap = new Map(areaTypeTemplates);
            const updatedCheckedTypes = new Set(checkedAreaTypes);
            
            for (const typeId of newTypeIds) {
                updatedCheckedTypes.add(typeId);
                
                try {
                    const typeRes = await axios.get(`${ENDPOINTS.AREA_TYPES_V2}/${typeId}`, { 
                        withCredentials: true 
                    });
                    const hasTemplate = typeRes.data?.data?.templateImage || typeRes.data?.templateImage;

                    if (hasTemplate) {
                        const imageUrl = `${ENDPOINTS.AREA_TYPES_V2}/${typeId}/template-image?t=${Date.now()}`;
                        updatedTemplateMap.set(typeId, imageUrl);
                    }
                } catch (err) {
                    console.error('Failed to check template for area type:', typeId, err);
                }
            }
            
            setCheckedAreaTypes(updatedCheckedTypes);
            setAreaTypeTemplates(updatedTemplateMap);
        })();
    }, [areasToDisplay, areaTypeTemplates, checkedAreaTypes]);

    const areaListLoading = loading || healthStatusState.loading || loadingTemplates;
    const hasAreasInStatusList = (['action-needed', 'attention', 'healthy', 'no-data'] as HealthStatus[])
        .some((statusKey) => statusCounts[statusKey] > 0);

    return (
        <div className="reports-page" style={{ position: 'relative' }}>
            {navLoading && (
                <div style={{
                    position: 'absolute',
                    inset: 0,
                    background: '#ffffff',
                    zIndex: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}>
                    <Spin tip="Loading report..." size="large" />
                </div>
            )}
            <Row justify="space-between" align="middle" className="reports-header">
                <Col>
                    <Space direction="vertical" size={0}>
                        <Title level={2} style={{ margin: 0 }}>Reports</Title>
                        <Text type="secondary" className="reports-subtitle">Generate reports for your areas</Text>
                    </Space>
                </Col>
                {currentReport && (
                    <Col>
                        <Space>
                            {navigationHistory.length > 0 && (
                                <Button 
                                    icon={<ArrowLeftOutlined />}
                                    onClick={() => handleBack()}
                                >
                                    Back
                                </Button>
                            )}
                            <Button 
                                icon={<HomeOutlined />}
                                onClick={handleHome}
                            >
                                Home
                            </Button>
                        </Space>
                    </Col>
                )}
            </Row>

            {/* Breadcrumb navigation */}
            {currentReport && (
                <div className="reports-breadcrumb">
                    <Breadcrumb
                        items={[
                            { title: (
                                <Button type="link" onClick={() => {
                                    // Pop all browser history entries we pushed, then reset
                                    if (navigationHistory.length > 0) {
                                        isProgrammaticNavRef.current = true;
                                        navigate(-navigationHistory.length);
                                    }
                                    handleHome();
                                }} style={{ padding: 0, height: 'auto' }}>
                                    <HomeOutlined /> Reports
                                </Button>
                            ) },
                            ...navigationHistory.map((nav, idx) => ({
                                title: (
                                    <Button
                                        type="link"
                                        onClick={() => {
                                            // Pop browser history entries back to this point
                                            const stepsBack = navigationHistory.length - idx;
                                            isProgrammaticNavRef.current = true;
                                            navigate(-stepsBack);
                                            // Restore this state
                                            setNavigationHistory(prev => prev.slice(0, idx));
                                            setSelectedArea(nav.area);
                                            setDateRange(nav.dateRange);
                                            setCurrentReport({
                                                template: nav.template,
                                                areaId: nav.area.id,
                                                dateFrom: nav.dateRange[0].toDate(),
                                                dateTo: nav.dateRange[1].toDate(),
                                                patternId: nav.patternId ?? null,
                                                patternPointId: nav.patternPointId ?? null,
                                                measurementId: nav.measurementId ?? null,
                                            });
                                            window.scrollTo({ top: 0, behavior: 'smooth' });
                                        }}
                                        style={{ padding: 0, height: 'auto' }}
                                    >
                                        {nav.area.name}
                                    </Button>
                                )
                            })),
                            // Current state — area name only (template in page title)
                            ...(selectedArea ? [{
                                title: (
                                    <span style={{ fontWeight: 500 }}>
                                        {selectedArea.name}
                                    </span>
                                )
                            }] : [])
                        ]}
                    />
                </div>
            )}

            {currentReport ? (
                <Card
                    className="reports-detail-card"
                    title={
                        selectedArea?.name
                            ? `Report: ${selectedArea.name}`
                            : currentReport.measurementId
                                ? `Report: Measurement ${currentReport.measurementId}`
                                : 'Report'
                    }
                    extra={(
                        <Button onClick={openSaveReportModal} disabled={savingReport}>
                            Save Report
                        </Button>
                    )}
                >
                    <Report
                        template={currentReport.template}
                        areaId={currentReport.areaId ?? undefined}
                        dateFrom={currentReport.dateFrom}
                        dateTo={currentReport.dateTo}
                        patternId={currentReport.patternId ?? undefined}
                        patternPointId={currentReport.patternPointId ?? undefined}
                        measurementId={currentReport.measurementId ?? undefined}
                        showControls={true}
                        onAreaClick={handleAreaClick}
                        onMetricClick={handleMetricClick}
                        onPatternClick={handlePatternClick}
                        onPatternPointClick={handlePatternPointClick}
                        onMeasurementClick={(measurementId) => navigate(`/measurements/${measurementId}`)}
                        onDateRangeChange={(range) => {
                            setDateRange(range);
                            setCurrentReport((prev: ReportTemplateState | null) =>
                                prev
                                    ? {
                                          ...prev,
                                          dateFrom: range[0].toDate(),
                                          dateTo: range[1].toDate(),
                                      }
                                    : prev
                            );
                        }}
                    />
                </Card>
            ) : (
                <Row gutter={[16, 16]}>
                    <Col xs={24} xl={11}>
                        <Card>
                            <Space direction="vertical" size={12} style={{ width: '100%' }}>
                                <Checkbox checked={showArchivedAreas} onChange={event => setShowArchivedAreas(event.target.checked)}>
                                    Show archived areas
                                </Checkbox>
                                <div className="reports-filter-bar">
                                    <Input.Search
                                        allowClear
                                        placeholder="Search areas"
                                        value={areaSearch}
                                        onChange={(event) => setAreaSearch(event.target.value)}
                                        className="reports-filter-input"
                                    />
                                </div>
                                <div className="reports-status-cards" aria-busy={areaListLoading}>
                                    {areaListLoading ? (
                                        <div className="reports-area-list-loading" role="status" aria-live="polite">
                                            <Spin size="large" />
                                            <Text type="secondary">
                                                {loadingTemplates ? 'Loading report...' : 'Loading areas...'}
                                            </Text>
                                        </div>
                                    ) : hasAreasInStatusList ? (
                                        (['action-needed', 'attention', 'healthy', 'no-data'] as HealthStatus[])
                                        .filter((statusKey) => statusCounts[statusKey] > 0)
                                        .map((statusKey) => {
                                            const meta = HEALTH_STATUS_CONFIG[statusKey];
                                            const isExpanded = expandedStatusCards.has(statusKey);
                                            return (
                                                <div key={`status-card-${statusKey}`} className={`reports-status-card reports-status-card--${statusKey}`}>
                                                    <div
                                                        className="reports-status-card-header"
                                                        role="button"
                                                        tabIndex={0}
                                                        onClick={() => toggleStatusCard(statusKey)}
                                                        onKeyDown={(event) => {
                                                            if (event.key === 'Enter' || event.key === ' ') {
                                                                toggleStatusCard(statusKey);
                                                            }
                                                        }}
                                                    >
                                                        <div className="reports-status-card-title">
                                                            <span className={`report-status-dot report-status-dot--${statusKey}`} />
                                                            <span>{meta.label}</span>
                                                        </div>
                                                        <div className="reports-status-card-meta">
                                                            <span className="reports-status-card-count">{statusCounts[statusKey]}</span>
                                                            {isExpanded ? <DownOutlined /> : <RightOutlined />}
                                                        </div>
                                                    </div>
                                                    {isExpanded && (
                                                        <Table
                                                            loading={loading}
                                                            dataSource={areasByStatus[statusKey]}
                                                            columns={columns}
                                                            className="reports-table"
                                                            size="small"
                                                            rowKey={(record) => String(record.id)}
                                                            pagination={false}
                                                            locale={{ emptyText: <Empty description="No areas found" /> }}
                                                            onRow={(record: Area) => ({
                                                                onClick: () => {
                                                                    const recordKey = String(record.id);
                                                                    fetchAreaDetail(record.id, areas);
                                                                    setSelectedArea(record);
                                                                    const path = findAreaPath(record.id, areas);
                                                                    const pathKeys = path.map((id) => String(id));
                                                                    setExpandedRowKeys((prev) => {
                                                                        if (selectedArea?.id === record.id && prev.includes(recordKey)) {
                                                                            return pathKeys.slice(0, -1);
                                                                        }
                                                                        return pathKeys;
                                                                    });
                                                                },
                                                                style: { cursor: 'pointer' }
                                                            })}
                                                            rowClassName={(record: Area) => {
                                                                const classes = [];
                                                                if (selectedArea?.id === record.id) classes.push('report-row-selected');
                                                                if ((record.hierarchyDepth || 0) > 0) classes.push('report-row-child');
                                                                return classes.join(' ');
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                            );
                                        })
                                    ) : (
                                        <Empty description="No areas found" />
                                    )}
                                </div>
                            </Space>
                        </Card>
                    </Col>
                    <Col xs={24} xl={13}>
                        <Space direction="vertical" size={12} style={{ width: '100%' }}>
                            <Card className="reports-overview-card">
                                {healthStatusState.loading ? (
                                    <div className="reports-overview-loading">
                                        <Spin size="small" />
                                        <Text type="secondary">Loading status overview...</Text>
                                    </div>
                                ) : healthStatusState.error ? (
                                    <Text type="danger">{healthStatusState.error}</Text>
                                ) : (
                                    <>
                                        <div className="reports-overview-header">
                                            <div>
                                                <Text type="secondary">Health overview</Text>
                                                <Title level={4} className="reports-overview-title">
                                                    {selectedAreaDetail?.name ?? selectedArea?.name ?? 'Area overview'}
                                                </Title>
                                            </div>
                                            <div className="reports-overview-meta">
                                                <Text type="secondary">Last scan</Text>
                                                <Text strong>
                                                    {overviewStats.lastScan
                                                        ? overviewStats.lastScan.format('MMM D, YYYY')
                                                        : 'No scans yet'}
                                                </Text>
                                            </div>
                                        </div>
                                        <div className="reports-overview-stats">
                                            {(['action-needed', 'attention', 'healthy', 'no-data'] as HealthStatus[]).map((statusKey) => {
                                                const meta = HEALTH_STATUS_CONFIG[statusKey];
                                                return (
                                                    <div key={statusKey} className={`reports-overview-stat reports-overview-stat--${statusKey}`}>
                                                        <span className={`report-status-dot report-status-dot--${statusKey}`} />
                                                        <span>{meta.label}</span>
                                                        <span className="reports-overview-value">{overviewStats.counts[statusKey]}</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </>
                                )}
                            </Card>
                            <Card title="Area map" className="reports-map-card">
                                <div className="reports-map-wrapper">
                                    <div className="reports-map-legend">
                                        <Text type="secondary">Health status (latest scan day)</Text>
                                        <div className="reports-map-legend-items">
                                            {(Object.keys(HEALTH_STATUS_CONFIG) as HealthStatus[]).map((statusKey) => {
                                                const meta = HEALTH_STATUS_CONFIG[statusKey];
                                                return (
                                                    <div key={`legend-${statusKey}`} className="reports-map-legend-item">
                                                        <span className={`report-status-dot report-status-dot--${statusKey}`} />
                                                        <span>{meta.label}</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        <Text type="secondary" className="reports-map-legend-note">
                                            Select an area from the list to focus the map.
                                        </Text>
                                    </div>
                                    <AreaMap
                                        mapKey={selectedAreaDetail?.id}
                                        areaGeometry={getGpsGeometryForAreaMap(selectedAreaDetail, selectedAreaDetail)}
                                        areaName={selectedAreaDetail?.name}
                                        initialViewState={viewState}
                                        allAreas={gpsAreasToDisplay}
                                        areaType={selectedAreaDetail?.areaType}
                                        areaTypeTemplates={areaTypeTemplates}
                                        selectedAreaId={selectedAreaDetail?.id ?? selectedArea?.id ?? null}
                                        areaStatusById={healthStatusByAreaId}
                                    />
                                </div>
                            </Card>
                            <ReportLatestDaySummaryCard
                                areas={summaryAreas.map((area) => ({
                                    id: area.id,
                                    name: area.name,
                                }))}
                                areaName={selectedAreaDetail?.name ?? selectedArea?.name ?? null}
                                summaries={latestSummariesForSelected}
                                loading={latestSummaryState.loading}
                                error={latestSummaryState.error}
                                healthStatusByAreaId={healthStatusByAreaId}
                                onSelectDateRange={(range, areaId) => {
                                    const targetArea =
                                        findAreaInTree(areaId, areas) ??
                                        (selectedAreaDetail?.id === areaId ? selectedAreaDetail : null) ??
                                        (selectedArea?.id === areaId ? selectedArea : null);
                                    setDateRange(range);
                                    if (targetArea) {
                                        handleGenerateReport(targetArea, false, range);
                                    }
                                }}
                            />
                        </Space>
                    </Col>
                </Row>
            )}

            <ReportChatAssistant
                areaId={chatAreaId}
                areaName={chatAreaName}
                dateRange={chatDateRange}
                measurementId={chatMeasurementId}
                patternId={chatPatternId}
                patternPointId={chatPatternPointId}
            />

            {/* Save Report Modal */}
            <Modal
                title="Save Report"
                open={showSaveReportModal}
                onCancel={() => {
                    setShowSaveReportModal(false);
                    saveReportForm.resetFields();
                }}
                onOk={handleSaveReport}
                okText="Save Report"
                confirmLoading={savingReport}
            >
                <Form form={saveReportForm} layout="vertical">
                    <Form.Item
                        label="Report title"
                        name="title"
                        rules={[
                            { required: true, message: 'Please enter a report title' },
                            { max: 200, message: 'Title must be 200 characters or less' },
                        ]}
                    >
                        <Input placeholder="Coverage report - Jan 2025" />
                    </Form.Item>
                    {canShareReports && (
                        <>
                            <Form.Item label="Share with company users" name="shareUserIds">
                                <Select
                                    mode="multiple"
                                    allowClear
                                    placeholder="Select users to share with"
                                    loading={loadingCompanyUsers}
                                    optionFilterProp="children"
                                >
                                    {companyUsers.map((user) => (
                                        <Select.Option key={user.id} value={user.id}>
                                            {user.email}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                            <Form.Item
                                label="Invite by email"
                                name="shareEmails"
                                rules={[
                                    {
                                        validator: (_rule, value: string[]) => {
                                            if (!value || value.length === 0) {
                                                return Promise.resolve();
                                            }
                                            const normalized = value
                                                .map((email) => String(email).trim())
                                                .filter((email) => email.length > 0);
                                            const invalid = normalized.filter(
                                                (email) => !SHARE_EMAIL_REGEX.test(email)
                                            );
                                            if (invalid.length > 0) {
                                                return Promise.reject(new Error('Enter valid email addresses'));
                                            }
                                            return Promise.resolve();
                                        },
                                    },
                                ]}
                            >
                                <Select
                                    mode="tags"
                                    tokenSeparators={[',', ' ']}
                                    placeholder="name@example.com"
                                />
                            </Form.Item>
                        </>
                    )}
                </Form>
            </Modal>

            {/* Template Selector Modal */}
            <Modal
                title={
                    selectedArea?.name
                        ? `Generate Report: ${selectedArea.name}`
                        : pendingMeasurementId
                            ? `Generate Report: Measurement ${pendingMeasurementId}`
                            : 'Generate Report'
                }
                open={showTemplateSelector}
                onCancel={handleCloseSelector}
                width={600}
                footer={[
                    <Button key="cancel" onClick={handleCloseSelector}>
                        Cancel
                    </Button>,
                ]}
            >
                {loadingTemplates ? (
                    <Spin tip="Loading templates..." />
                ) : (
                    <Space direction="vertical" style={{ width: '100%' }} size="large">
                        {templates.length === 0 ? (
                            <Alert
                                message="No Templates Available"
                                description={
                                    selectedArea
                                        ? 'There are no report templates configured for this area type. Contact your administrator to create templates.'
                                        : 'There are no report templates configured for this measurement.'
                                }
                                type="warning"
                                showIcon
                            />
                        ) : (
                            <>
                                <Form layout="vertical">
                                    <Form.Item label="Date Range" required>
                                        <RangePicker
                                            value={dateRange}
                                            onChange={(dates) => {
                                                if (dates && dates[0] && dates[1]) {
                                                    setDateRange([dates[0], dates[1]]);
                                                }
                                            }}
                                            style={{ width: '100%' }}
                                            size="large"
                                            presets={[
                                                { label: 'Last 7 days', value: [dayjs().subtract(6, 'day'), dayjs()] },
                                                { label: 'Last 30 days', value: [dayjs().subtract(29, 'day'), dayjs()] },
                                                { label: 'Last 90 days', value: [dayjs().subtract(89, 'day'), dayjs()] },
                                                { label: 'This Month', value: [dayjs().startOf('month'), dayjs()] },
                                                { label: 'Last Month', value: [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')] },
                                            ]}
                                        />
                                    </Form.Item>
                                </Form>
                                <List
                                    dataSource={templates}
                                    bordered
                                    renderItem={(template) => (
                                        <List.Item
                                            onClick={() => {
                                                setSelectedTemplate(template);
                                                handleConfirmGenerate(template);
                                            }}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <List.Item.Meta
                                                title={
                                                    <Space size="small">
                                                        <Text strong>{template.name}</Text>
                                                        {template.isGlobal ? (
                                                            <Tag color="blue">Global</Tag>
                                                        ) : template.company?.name ? (
                                                            <Tag>{template.company.name}</Tag>
                                                        ) : null}
                                                    </Space>
                                                }
                                                description={
                                                    <Space direction="vertical" size={2}>
                                                        {template.description && <Text>{template.description}</Text>}
                                                        <Text type="secondary">
                                                            Sections: {template.sections?.length || 0}
                                                        </Text>
                                                    </Space>
                                                }
                                            />
                                        </List.Item>
                                    )}
                                />
                            </>
                        )}
                    </Space>
                )}
            </Modal>
        </div>
    );
};

export default Reports;
